import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { useAuth } from '../../../context/AuthContext';
import { getAdminContent, deleteContent } from '../../../services/contentService';
import BulkUploadModal from '../../../components/BulkUploadModal';

const ContentPage = () => {
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const { token } = useAuth();

    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        try {
            setLoading(true);
            // Always fetch only blog content
            const response = await getAdminContent(token, 'blog');
            setContent(response.data);
        } catch (error) {
            console.error('Error fetching content:', error);
            toast.error('Error fetching content');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                await deleteContent(id, token);
                toast.success('Content deleted successfully');
                fetchContent();
            } catch (error) {
                console.error('Error deleting content:', error);
                toast.error('Error deleting content');
            }
        }
    };

    const getStatusBadge = (status) => {
        const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
        if (status === 'published') {
            return `${baseClasses} bg-green-100 text-green-800`;
        }
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
    };

    const getTypeBadge = (type) => {
        const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
        const colors = {
            legal: 'bg-red-100 text-red-800',
            blog: 'bg-blue-100 text-blue-800',
            info: 'bg-purple-100 text-purple-800'
        };
        return `${baseClasses} ${colors[type] || 'bg-gray-100 text-gray-800'}`;
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Blog Management</h1>
                        <p className="text-gray-600">Create and manage blog posts for "Blog" page</p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={() => setShowBulkUpload(true)}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Bulk Upload
                        </button>
                        <Link
                            to="/admin/content/new"
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Add New Blog Post
                        </Link>
                    </div>
                </div>

                {/* Info Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-blue-800 font-medium">Blog Content Management</p>
                    </div>
                    <p className="text-blue-700 text-sm mt-1">Manage your blog posts and articles. Create engaging content that will appear on the "Blog" page.</p>
                </div>

                {/* Content Table */}
                <div className="bg-[#f8f8f8] shadow-sm rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '800px' }}>
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                                    Title
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                                    Slug
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                    Updated
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                            {content.map((item) => (
                                <tr key={item._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 w-1/3">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {item.title}
                                            </div>
                                            {item.description && (
                                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                                    {item.description}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-1/4">
                                        /{item.slug}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-20">
                                        <span className={getTypeBadge(item.contentType)}>
                                            {item.contentType}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-20">
                                        <span className={getStatusBadge(item.status)}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
                                        {new Date(item.updatedAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium w-24">
                                        <div className="flex space-x-3">
                                            <Link
                                                to={`/admin/content/${item._id}/view`}
                                                className="text-gray-600 hover:text-primary-600"
                                                title="View content"
                                            >
                                                <FiEye size={18} />
                                            </Link>
                                            <Link
                                                to={`/admin/content/${item._id}/edit`}
                                                className="text-gray-600 hover:text-primary-600"
                                                title="Edit content"
                                            >
                                                <FiEdit size={18} />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(item._id)}
                                                className="text-gray-600 hover:text-primary-600"
                                                title="Delete content"
                                            >
                                                <FiTrash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {content.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No blog posts found</p>
                            <Link
                                to="/admin/content/new"
                                className="text-blue-600 hover:text-blue-800 mt-2 inline-block"
                            >
                                Create your first blog post
                            </Link>
                        </div>
                    )}
                </div>

                {/* Bulk Upload Modal */}
                <BulkUploadModal
                    isOpen={showBulkUpload}
                    onClose={() => setShowBulkUpload(false)}
                    onSuccess={() => {
                        fetchContent();
                        setShowBulkUpload(false);
                    }}
                />
            </div>
        </AdminLayout>
    );
};

export default ContentPage;
